import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraAltIcon,
  CloudDownload as CloudDownloadIcon,
  Download as DownloadIcon,
  PhotoCamera as PhotoCameraIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import appConfig from '../../../config/appConfig';
import { useCanManageLeague } from '../../../contexts/PermissionsContext';

const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const apiBase = appConfig.apiBaseUrl || 'http://localhost:5131';
  return `${apiBase}${url}`;
};

const getInitials = (member) => {
  const firstInitial = member.firstName?.[0] || '';
  const lastInitial = member.lastName?.[0] || '';
  return `${firstInitial}${lastInitial}` || '?';
};

const getMemberSortName = (member) => (
  member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email || ''
);

const getUpdatedAtTime = (member) => {
  if (!member.profilePhotoUpdatedAt) return null;
  const time = new Date(member.profilePhotoUpdatedAt).getTime();
  return Number.isNaN(time) ? null : time;
};

const formatUpdatedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getMemberSearchText = (member) => [
  member.firstName,
  member.lastName,
  member.fullName,
  member.email,
].filter(Boolean).join(' ').toLowerCase();

const LeagueProfilePhotosPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canManageLeague = useCanManageLeague(parseInt(leagueId, 10));

  const fileInputRef = useRef(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [exporting, setExporting] = useState(false);
  const [selectedUploadUserTenantId, setSelectedUploadUserTenantId] = useState(null);
  const [uploadingUserTenantId, setUploadingUserTenantId] = useState(null);
  const [downloadingUserTenantId, setDownloadingUserTenantId] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!leagueId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await unifiedApi.getLeagueProfilePhotos(leagueId);
      setReport(data);
    } catch (err) {
      console.error('Failed to load league profile photos', err);
      setError(err.message || 'Failed to load profile photo report.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const visibleMembers = useMemo(() => {
    const members = report?.members || [];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return members
      .filter((member) => {
        if (filter === 'with-photo') return member.hasProfilePhoto;
        if (filter === 'missing-photo') return !member.hasProfilePhoto;
        return true;
      })
      .filter((member) => {
        if (!normalizedSearch) return true;
        return getMemberSearchText(member).includes(normalizedSearch);
      })
      .slice()
      .sort((firstMember, secondMember) => {
        let comparison;
        if (sortBy === 'updated') {
          const firstTime = getUpdatedAtTime(firstMember);
          const secondTime = getUpdatedAtTime(secondMember);
          // Members without an upload date always sort last, regardless of direction.
          if (firstTime == null && secondTime == null) {
            return getMemberSortName(firstMember).localeCompare(
              getMemberSortName(secondMember),
              undefined,
              { sensitivity: 'base' }
            );
          }
          if (firstTime == null) {
            return 1;
          }
          if (secondTime == null) {
            return -1;
          }
          comparison = firstTime - secondTime;
          if (comparison === 0) {
            comparison = getMemberSortName(firstMember).localeCompare(
              getMemberSortName(secondMember),
              undefined,
              { sensitivity: 'base' }
            );
          }
        } else {
          comparison = getMemberSortName(firstMember).localeCompare(
            getMemberSortName(secondMember),
            undefined,
            { sensitivity: 'base' }
          );
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [filter, report?.members, searchTerm, sortBy, sortDirection]);

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    // Default new sort direction: descending for "updated" (most recent first), ascending otherwise.
    setSortDirection(column === 'updated' ? 'desc' : 'asc');
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      const blob = await unifiedApi.downloadLeagueProfilePhotosZip(leagueId);
      if (!blob || blob.size === 0) {
        setError('No profile photos were available to export.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `league-${leagueId}-profile-photos.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('Profile photo export started.');
    } catch (err) {
      console.error('Failed to export profile photos', err);
      setError(err.message || 'Failed to export profile photos.');
    } finally {
      setExporting(false);
    }
  };

  const handleUploadClick = (userTenantId) => {
    setSelectedUploadUserTenantId(userTenantId);
    fileInputRef.current?.click();
  };

  const buildPhotoFileName = (member) => {
    const sanitize = (value) => (value || '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const first = sanitize(member.firstName);
    const last = sanitize(member.lastName);
    const base = [first, last].filter(Boolean).join('_') || `member_${member.userTenantId}`;
    return `${base}.jpg`;
  };

  const handleDownloadPhoto = async (member) => {
    if (!member?.hasProfilePhoto) return;

    setDownloadingUserTenantId(member.userTenantId);
    setError(null);
    setSuccess(null);

    try {
      const blob = await unifiedApi.downloadLeagueProfilePhoto(leagueId, member.userTenantId);
      if (!blob || blob.size === 0) {
        setError('Profile photo is not available to download.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildPhotoFileName(member);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download profile photo', err);
      setError(err.message || 'Failed to download profile photo.');
    } finally {
      setDownloadingUserTenantId(null);
    }
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    const userTenantId = selectedUploadUserTenantId;

    if (!file || !userTenantId) {
      event.target.value = '';
      return;
    }

    if (!allowedImageTypes.includes(file.type)) {
      setError('Please select an image file (JPEG, PNG, GIF, or WebP).');
      setSelectedUploadUserTenantId(null);
      event.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size cannot exceed 10MB.');
      setSelectedUploadUserTenantId(null);
      event.target.value = '';
      return;
    }

    setError(null);
    setSuccess(null);
    setUploadingUserTenantId(userTenantId);

    try {
      await unifiedApi.uploadLeagueProfilePhoto(leagueId, userTenantId, file);
      setSuccess('Profile photo uploaded.');
      await fetchReport();
    } catch (err) {
      console.error('Failed to upload profile photo', err);
      setError(err.message || 'Failed to upload profile photo.');
    } finally {
      setSelectedUploadUserTenantId(null);
      setUploadingUserTenantId(null);
      event.target.value = '';
    }
  };

  const renderSourceChips = (member) => (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {(member.membershipSources || []).map(source => (
        <Chip key={source} label={source} size="small" clickable={false} onClick={() => {}} />
      ))}
    </Stack>
  );

  const renderStatusChip = (member) => (
    <Chip
      label={member.hasProfilePhoto ? 'Has photo' : 'Missing'}
      color={member.hasProfilePhoto ? 'success' : 'warning'}
      size="small"
      clickable={false}
      onClick={() => {}}
    />
  );

  const renderUploadButton = (member) => {
    if (!canManageLeague) return null;

    const isUploading = uploadingUserTenantId === member.userTenantId;
    return (
      <Tooltip title={member.hasProfilePhoto ? 'Replace profile photo' : 'Upload profile photo'}>
        <span>
          <IconButton
            aria-label={`Upload profile photo for ${member.fullName}`}
            onClick={() => handleUploadClick(member.userTenantId)}
            disabled={isUploading}
            color="primary"
            size="small"
          >
            {isUploading ? <CircularProgress size={20} /> : <CameraAltIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderDownloadButton = (member) => {
    if (!member.hasProfilePhoto) return null;

    const isDownloading = downloadingUserTenantId === member.userTenantId;
    return (
      <Tooltip title="Download profile photo">
        <span>
          <IconButton
            aria-label={`Download profile photo for ${member.fullName}`}
            onClick={() => handleDownloadPhoto(member)}
            disabled={isDownloading}
            color="primary"
            size="small"
          >
            {isDownloading ? <CircularProgress size={20} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderRowActions = (member) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      {renderDownloadButton(member)}
      {renderUploadButton(member)}
    </Stack>
  );

  const renderMemberAvatar = (member) => (
    <Avatar src={getImageUrl(member.profilePhotoUrl)} sx={{ width: 44, height: 44, bgcolor: 'primary.main' }}>
      {getInitials(member)}
    </Avatar>
  );

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <PhotoCameraIcon color="primary" />
              {report?.leagueName ? `${report.leagueName} - Profile Photos` : 'Profile Photos'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review league member photo coverage and export available photos.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading || !report?.withPhotoCount}
            fullWidth={isMobile}
          >
            {exporting ? 'Exporting...' : 'Export ZIP'}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        aria-label="Profile photo file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="h5">{report?.totalMembers ?? 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total members</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="h5">{report?.withPhotoCount ?? 0}</Typography>
              <Typography variant="body2" color="text.secondary">With photos</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="h5">{report?.missingPhotoCount ?? 0}</Typography>
              <Typography variant="body2" color="text.secondary">Missing photos</Typography>
            </Paper>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <InputLabel>Photo Status</InputLabel>
              <Select value={filter} label="Photo Status" onChange={(event) => setFilter(event.target.value)}>
                <MenuItem value="all">All Members</MenuItem>
                <MenuItem value="with-photo">Has Photo</MenuItem>
                <MenuItem value="missing-photo">Missing Photo</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Search members"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ flex: 1, minWidth: { xs: '100%', md: 260 } }}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon color="action" fontSize="small" sx={{ mr: 1 }} />,
                },
              }}
            />
            {isMobile && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={`${sortBy}-${sortDirection}`}
                  label="Sort by"
                  onChange={(event) => {
                    const [nextSortBy, nextDirection] = event.target.value.split('-');
                    setSortBy(nextSortBy);
                    setSortDirection(nextDirection);
                  }}
                >
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                  <MenuItem value="updated-desc">Photo updated (newest)</MenuItem>
                  <MenuItem value="updated-asc">Photo updated (oldest)</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button variant="outlined" onClick={fetchReport} fullWidth={isMobile}>Refresh</Button>
          </Stack>

          {isMobile ? (
            <Stack spacing={2}>
              {visibleMembers.map(member => (
                <Paper key={member.userTenantId} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                    {renderMemberAvatar(member)}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography component={Link} to={`/player/${member.userTenantId}`} variant="subtitle1" sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-word' }}>
                        {member.fullName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {member.email}
                      </Typography>
                    </Box>
                    {renderRowActions(member)}
                  </Stack>
                  <Stack spacing={1}>
                    {renderStatusChip(member)}
                    {member.profilePhotoUpdatedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Photo updated: {formatUpdatedAt(member.profilePhotoUpdatedAt)}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Teams: {member.teamNames?.length ? member.teamNames.join(', ') : 'Not rostered'}
                    </Typography>
                    {renderSourceChips(member)}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Photo</TableCell>
                    <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortDirection : 'asc'}
                        onClick={() => handleSortChange('name')}
                      >
                        Member
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Teams</TableCell>
                    <TableCell>Sources</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sortDirection={sortBy === 'updated' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'updated'}
                        direction={sortBy === 'updated' ? sortDirection : 'desc'}
                        onClick={() => handleSortChange('updated')}
                      >
                        Photo updated
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleMembers.map(member => (
                    <TableRow key={member.userTenantId} hover>
                      <TableCell>{renderMemberAvatar(member)}</TableCell>
                      <TableCell>
                        <Typography component={Link} to={`/player/${member.userTenantId}`} sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}>
                          {member.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {member.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{member.teamNames?.length ? member.teamNames.join(', ') : 'Not rostered'}</TableCell>
                      <TableCell>{renderSourceChips(member)}</TableCell>
                      <TableCell>{renderStatusChip(member)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={member.profilePhotoUpdatedAt ? 'text.primary' : 'text.disabled'}>
                          {formatUpdatedAt(member.profilePhotoUpdatedAt) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{renderRowActions(member)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {visibleMembers.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No members match the selected filter.
            </Alert>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default LeagueProfilePhotosPage;
